
import { useState, useEffect, useRef } from 'react';
import { pricingPlans } from '../../../mocks/pricing';

export default function Prices() {
  const [selectedPlan, setSelectedPlan] = useState(1);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [cardsVisible, setCardsVisible] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs1 = new IntersectionObserver(([e]) => { if (e.isIntersecting) setHeaderVisible(true); }, { threshold: 0.1 });
    const obs2 = new IntersectionObserver(([e]) => { if (e.isIntersecting) setCardsVisible(true); }, { threshold: 0.05 });
    if (headerRef.current) obs1.observe(headerRef.current);
    if (cardsRef.current) obs2.observe(cardsRef.current);
    return () => { obs1.disconnect(); obs2.disconnect(); };
  }, []);

  return (
    <section
      id="prices"
      className="py-14 md:py-24 bg-gradient-to-br from-slate-50 via-teal-50/20 to-slate-50 relative overflow-hidden"
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-48 md:w-72 h-48 md:h-72 bg-teal-400/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-10 w-64 md:w-96 h-64 md:h-96 bg-emerald-400/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header — slides from right */}
        <div
          ref={headerRef}
          className={`text-center mb-10 md:mb-16 transition-all duration-800 ease-out ${
            headerVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-16'
          }`}
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-3 md:mb-4 bg-gradient-to-r from-slate-900 via-teal-800 to-slate-900 bg-clip-text text-transparent">
            Прозрачные цены
          </h2>
          <div className={`h-1 mx-auto rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-1000 delay-300 ${headerVisible ? 'w-24' : 'w-0'}`}></div>
          <p className={`text-sm md:text-xl text-slate-600 max-w-2xl mx-auto mt-3 transition-all duration-700 delay-200 ${headerVisible ? 'opacity-100' : 'opacity-0'}`}>
            Выберите оптимальный тариф для ремонта вашей техники
          </p>
        </div>

        {/* Cards — flip in one by one */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8 max-w-6xl mx-auto" ref={cardsRef}>
          {pricingPlans.map((plan, index) => (
            <div
              key={plan.id}
              className={`relative group transition-all duration-700 ${
                cardsVisible ? 'opacity-100 translate-y-0 rotate-0' : 'opacity-0 translate-y-20 rotate-1'
              }`}
              style={{
                transitionDelay: `${index * 200}ms`,
                transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
              }}
              onMouseEnter={() => setSelectedPlan(plan.id)}
            >
              {plan.popular && (
                <div className="absolute -top-4 md:-top-5 left-1/2 -translate-x-1/2 z-20">
                  <div className={`bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-4 md:px-6 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-semibold shadow-lg animate-pulse-slow whitespace-nowrap transition-all duration-500 ${cardsVisible ? 'scale-100' : 'scale-0'}`} style={{ transitionDelay: '600ms' }}>
                    ⭐ Популярный
                  </div>
                </div>
              )}

              <div
                className={`relative h-full bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 transition-all duration-500 ${
                  selectedPlan === plan.id || plan.popular
                    ? 'shadow-2xl scale-[1.02] md:scale-105 -translate-y-1 md:-translate-y-2'
                    : 'shadow-lg hover:shadow-2xl hover:scale-[1.02] md:hover:scale-105'
                }`}
              >
                <div
                  className={`absolute inset-0 rounded-2xl md:rounded-3xl bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl ${
                    plan.popular ? 'opacity-30' : ''
                  }`}
                ></div>

                <div className="relative z-10">
                  <div
                    className={`w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-gradient-to-br ${
                      plan.popular ? 'from-teal-500 to-emerald-500' : 'from-slate-500 to-slate-700'
                    } flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}
                  >
                    <i className={`${plan.icon} text-xl md:text-2xl text-white`}></i>
                  </div>

                  <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-1 md:mb-2">{plan.name}</h3>
                  <p className="text-slate-600 text-sm mb-4 md:mb-6">{plan.description}</p>

                  <div className="mb-6 md:mb-8">
                    <div className="flex items-baseline gap-1.5 md:gap-2">
                      <span className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">
                        {plan.price}
                      </span>
                      <span className="text-slate-600">₸</span>
                    </div>
                    <p className="text-xs md:text-sm text-slate-500 mt-1 md:mt-2">{plan.priceNote}</p>
                  </div>

                  <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                    {plan.features.map((feature, idx) => (
                      <li
                        key={idx}
                        className={`flex items-start gap-2.5 md:gap-3 transition-all duration-500 ${
                          cardsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
                        }`}
                        style={{ transitionDelay: `${index * 200 + 400 + idx * 80}ms` }}
                      >
                        <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-white text-xs md:text-sm"></i>
                        </div>
                        <span className="text-slate-700 text-sm md:text-base">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    className={`w-full py-3 md:py-4 rounded-xl font-semibold text-sm md:text-base transition-all duration-500 relative overflow-hidden whitespace-nowrap cursor-pointer ${
                      plan.popular
                        ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-lg hover:shadow-2xl hover:scale-105'
                        : 'bg-slate-100 text-slate-900 hover:bg-slate-200'
                    }`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <span className="relative z-10">Выбрать тариф</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={`text-center mt-8 md:mt-12 transition-all duration-700 delay-700 ${cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-slate-600 text-xs md:text-base">
            💡 Все цены указаны за базовую работу. Стоимость запчастей рассчитывается отдельно.
          </p>
        </div>
      </div>
    </section>
  );
}
